import React from 'react';

/**
 * Displays product price with optional discount styling.
 * Format: [Discounted Price] [Original Price Strikethrough] ([X% OFF] or custom label)
 */
const ProductPriceDisplay = ({
    regularPrice,
    discount,
    quantity = 1,
    salePrice: salePriceProp,
    regularPriceIsSalePrice = false,
    /** Absolute discount amount for the line (qty already included) — final applied amount */
    discountAmount,
    /** Custom badge e.g. "(Offer 15% OFF)" */
    discountLabel,
    currency = 'KWD',
    currencySymbol = 'KD',
}) => {
    if (regularPrice == null) return null;

    const qty = Math.max(1, Number(quantity) || 1);
    const unitRegular = Number(regularPrice) || 0;
    const lineOriginal = unitRegular * qty;
    const amt = Number(discountAmount);
    const hasAmt = Number.isFinite(amt) && amt > 0;
    const hasPct = discount != null && discount !== '' && Number(discount) > 0;
    const pct = hasPct ? Number(discount) / 100 : 0;

    let lineTotal = lineOriginal;
    let showDiscount = false;

    if (hasAmt) {
        lineTotal = Math.max(0, Math.round((lineOriginal - amt) * 1000) / 1000);
        showDiscount = lineTotal < lineOriginal - 0.001;
    } else if (salePriceProp != null && hasPct) {
        const unitSale = Number(salePriceProp);
        lineTotal = unitSale * qty;
        showDiscount = hasPct && unitSale < unitRegular;
    } else if (regularPriceIsSalePrice && hasPct) {
        // regularPrice is already the sale unit price
        const unitSale = unitRegular;
        const unitOrig = unitSale / (1 - pct);
        lineTotal = unitSale * qty;
        // overwrite original for strike
        const lineOrigFromSale = unitOrig * qty;
        const formatPrice = (val) =>
            val.toLocaleString('en-US', {
                style: 'currency',
                currency,
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
            }).replace('KWD', currencySymbol);

        const badge = discountLabel || `(${Math.round(Number(discount))}% OFF)`;
        return (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0">
                <span className="!font-cardillac text-base-18 font-500 text-dark">
                    {formatPrice(lineTotal)}
                </span>
                {qty > 1 && (
                    <span className="text-gray-dark font-400 text-sm">
                        ({formatPrice(unitSale)} × {qty})
                    </span>
                )}
                <span className="line-through text-gray-dark font-400 text-sm">
                    {formatPrice(lineOrigFromSale)}
                </span>
                <span className="text-red-500 font-600 text-sm">{badge}</span>
            </div>
        );
    } else if (hasPct) {
        lineTotal = Math.round(unitRegular * (1 - pct) * qty * 1000) / 1000;
        showDiscount = true;
    } else if (salePriceProp != null && Number(salePriceProp) < unitRegular) {
        lineTotal = Number(salePriceProp) * qty;
        showDiscount = true;
    }

    const formatPrice = (val) =>
        val.toLocaleString('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
        }).replace('KWD', currencySymbol);

    const unitDisplay = lineTotal / qty;
    const badge = discountLabel
        || (hasPct ? `(${Math.round(Number(discount))}% OFF)` : null);

    return (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0">
            <span className="!font-cardillac text-base-18 font-500 text-dark">
                {formatPrice(lineTotal)}
            </span>
            {qty > 1 && (
                <span className="text-gray-dark font-400 text-sm">
                    ({formatPrice(unitDisplay)} × {qty})
                </span>
            )}
            {showDiscount && (
                <>
                    <span className="line-through text-gray-dark font-400 text-sm">
                        {formatPrice(lineOriginal)}
                    </span>
                    {badge ? (
                        <span className="text-red-500 font-600 text-sm">{badge}</span>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default ProductPriceDisplay;
